import React, { useEffect, useMemo, useState } from 'react'
import { Database, Thermometer, Zap, RefreshCw, Filter } from 'lucide-react'
import { api as axios } from '../lib/api'
import { useI18n } from '../i18n/i18n'

type SummaryResp = {
  totalRecords: number
  lastTimestamp: string | null
  averages: { temperature_c: number | null; power_kw: number | null }
  timeRange: { hours: number; from: string | null; to: string | null }
  metadata: { schema: string; table: string; queriedAt: string }
}

type RecordRow = {
  timestamp: string | null
  location: string | null
  temperature_c: number | null
  power_kw: number | null
  relative_humidity_per100: number | null
}

const periods = [
  { value: '1h', label: '1H' },
  { value: '4h', label: '4H' },
  { value: '12h', label: '12H' },
  { value: '24h', label: '24H' },
  { value: '3d', label: '3D' },
  { value: '1w', label: '1W' },
  { value: '1M', label: '1M' }
]

const SchneiderWidget: React.FC = () => {
  const { t } = useI18n()
  const [summary, setSummary] = useState<SummaryResp | null>(null)
  const [records, setRecords] = useState<RecordRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState('24h')
  const [location, setLocation] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 10
  const [hasMore, setHasMore] = useState(false)

  const isFiltered = useMemo(() => location.trim() !== '', [location])

  const refreshSummary = async () => {
    setError(null)
    try {
      const { data } = await axios.get<SummaryResp>(`/api/schneider/summary`, {
        params: { period: selectedPeriod }
      })
      setSummary(data)
    } catch (e) {
      setError(t('sch.error.summary'))
    }
  }

  const refreshRows = async () => {
    setError(null)
    if (isFiltered) {
      try {
        const { data } = await axios.get<{ records: RecordRow[]; hasMore?: boolean }>(`/api/schneider/filter`, {
          params: {
            location: location || undefined,
            page,
            pageSize
          }
        })
        setRecords(data.records || [])
        setHasMore(Boolean((data as any).hasMore) || (data.records?.length ?? 0) === pageSize)
      } catch (e) {
        setError(t('sch.error.filtered'))
      }
    } else {
      try {
        const { data } = await axios.get<{ records: RecordRow[]; hasMore?: boolean }>(`/api/schneider/recent`, {
          params: { page, pageSize }
        })
        setRecords(data.records || [])
        setHasMore(Boolean((data as any).hasMore) || (data.records?.length ?? 0) === pageSize)
      } catch (e) {
        setError(t('sch.error.recent'))
      }
    }
  }

  const refreshAll = async () => {
    setLoading(true)
    await Promise.all([refreshSummary(), refreshRows()])
    setLoading(false)
  }

  useEffect(() => {
    refreshAll()
    const timer = setInterval(refreshSummary, 60000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPage(1)
    refreshSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod])

  const formatTs = (ts: string | null) => (ts ? new Date(ts).toLocaleString() : '—')

  return (
    <div className="card card-hover p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Database className="h-6 w-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t('sch.title')}</h3>
        </div>
        <button
          onClick={refreshAll}
          disabled={loading}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters and period */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">{t('sch.location')}</label>
          <input className="input-field w-full text-sm" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. DC1" />
        </div>
        <div className="flex items-end">
          <div className="flex items-center gap-2">
            <button onClick={() => { if (page > 1) { setPage(page - 1); refreshRows() } }} disabled={page <= 1} className="px-3 py-2 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">Prev</button>
            <span className="text-xs text-gray-600">Page {page}</span>
            <button onClick={() => { if (hasMore) { setPage(page + 1); refreshRows() } }} disabled={!hasMore} className="px-3 py-2 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">{t('sch.period')}</label>
          <div className="flex flex-wrap gap-1">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => setSelectedPeriod(p.value)}
                className={`px-2 py-1 text-xs rounded ${selectedPeriod === p.value ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Filter className="h-4 w-4" />
          <span>{isFiltered ? t('sch.filters.active') : t('sch.filters.none')}</span>
        </div>
        <div className="space-x-2">
          <button onClick={() => { setPage(1); refreshRows() }} className="btn-primary text-xs">{t('sch.apply')}</button>
          {isFiltered && (
            <button onClick={() => { setLocation(''); setPage(1); refreshRows(); }} className="text-xs px-3 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">{t('sch.clear')}</button>
          )}
        </div>
      </div>


      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-4 rounded-lg">
          <div className="text-xs text-primary-700">{t('sch.kpi.total')}</div>
          <div className="text-2xl font-semibold text-primary-800">{summary?.totalRecords?.toLocaleString() ?? '—'}</div>
        </div>
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg">
          <div className="flex items-center space-x-2 text-xs text-orange-700"><Thermometer className="h-4 w-4" /><span>Avg Temp (°C)</span></div>
          <div className="text-2xl font-semibold text-orange-800">{summary?.averages.temperature_c ?? '—'}</div>
        </div>
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 p-4 rounded-lg">
          <div className="flex items-center space-x-2 text-xs text-emerald-700"><Zap className="h-4 w-4" /><span>Avg Power (kW)</span></div>
          <div className="text-2xl font-semibold text-emerald-800">{summary?.averages.power_kw ?? '—'}</div>
        </div>
      </div>

      {/* Table */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 mb-3">{error}</div>
      )}

      <div className="overflow-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Timestamp</th>
              <th className="px-3 py-2 text-left">Location</th>
              <th className="px-3 py-2 text-right">Temp (°C)</th>
              <th className="px-3 py-2 text-right">Horas Motor (hrs)</th>
              <th className="px-3 py-2 text-right">Power (kW)</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td className="px-3 py-3 text-center text-gray-500" colSpan={6}>No data</td>
              </tr>
            ) : (
              records.map((r, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 whitespace-nowrap">{formatTs(r.timestamp)}</td>
                  <td className="px-3 py-2">{r.location ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.temperature_c ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.relative_humidity_per100 ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.power_kw ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default SchneiderWidget
